import { IImageEmbedFieldsFragment } from '../../../utils/apollo/graphqlSchema';
import { ForwardedRef, useImperativeHandle, useState } from 'react';
import {
  ICreateImageEmbedUploadConfig,
  IImageEmbedUploadHandle,
} from './types';
import { assertCondition, hofForwardRef } from '../../../utils/types';
import {
  useAbortAll,
  useItemAbortListener,
  useItemErrorListener,
  useItemFinishListener,
  useItemProgressListener,
  useItemStartListener,
  useRequestPreSend,
  useUploady,
} from '@rpldy/uploady';
import { freeze } from '@reduxjs/toolkit';
import { IUploadState, UploadStateType } from '../types';
import UploadStateModal from '../UploadStateModal';
import { MaybeNil } from '../../../utils/interfaces';

interface IProps<Context> {
  onUploadSuccess: (
    imageEmbed: IImageEmbedFieldsFragment,
    context: Context,
  ) => void;
  onUploadFailed?: (
    imageEmbed: MaybeNil<IImageEmbedFieldsFragment>,
    context: Context,
  ) => void;
  createImageEmbedUploadConfig: (
    context: Context,
  ) => Promise<ICreateImageEmbedUploadConfig>;
}

const initialUploadState = freeze<IUploadState>({
  type: UploadStateType.INACTIVE,
});

const ImageEmbedUpload = hofForwardRef(
  <Context,>(
    {
      onUploadSuccess,
      onUploadFailed,
      createImageEmbedUploadConfig,
    }: IProps<Context>,
    ref: ForwardedRef<IImageEmbedUploadHandle<Context>>,
  ) => {
    const [uploadState, setUploadState] =
      useState<IUploadState>(initialUploadState);

    const { showFileUpload } = useUploady();

    useRequestPreSend(async ({ items, options }) => {
      assertCondition(items.length === 1);
      const item = items[0];

      setUploadState({
        type: UploadStateType.PREPARING,
      });

      let uploadConfig: ICreateImageEmbedUploadConfig;
      try {
        uploadConfig = await createImageEmbedUploadConfig(
          options.userData.context as Context,
        );
      } catch (e) {
        setUploadState({
          type: UploadStateType.ERROR,
          message:
            'Failed to prepare the upload. Please reload the page and try again',
        });
        onUploadFailed?.(null, options.userData.context as Context);
        return;
      }

      const {
        amzAlgorithm,
        amzCredential,
        amzDate,
        amzSignature,
        amzSecurityToken,
        endpoint,
        key,
        policy,
      } = uploadConfig.postUploadConfig;

      return {
        items,
        options: {
          destination: {
            url: endpoint,
            method: 'POST',
            params: {
              key,
              'x-amz-date': amzDate,
              'x-amz-algorithm': amzAlgorithm,
              'x-amz-credential': amzCredential,
              'x-amz-signature': amzSignature,
              // setting to undefined will skip it from including it in the
              // form params if not present (security tokens are only for
              // INT/STG/PROD, not required for dev environments
              // https://docs.aws.amazon.com/AmazonS3/latest/userguide/
              //  RESTAuthentication.html#UsingTemporarySecurityCredentials )
              'x-amz-security-token': amzSecurityToken ?? undefined,
              policy,
              'content-type': item.file.type,
            },
            filesParamName: 'file',
          },
          userData: {
            imageEmbed: uploadConfig.imageEmbed,
          },
        },
      };
    });

    useItemStartListener(() => {
      setUploadState({ type: UploadStateType.UPLOADING, progress: 0 });
    });

    useItemProgressListener((item) => {
      setUploadState({
        type: UploadStateType.UPLOADING,
        progress: item.completed,
      });
    });

    useItemFinishListener((_, options) => {
      setUploadState(initialUploadState);
      // Unsafe casts because `userData` is any. Ideally, there should
      // be zod validation, but it is hard to validate Context which is generic
      onUploadSuccess(
        options.userData.imageEmbed as IImageEmbedFieldsFragment,
        options.userData.context as Context,
      );
    });

    useItemErrorListener((item, options) => {
      console.error('Failed to upload', { item, options });
      setUploadState({
        type: UploadStateType.ERROR,
        message:
          'Failed to upload file, please try again. ' +
          'Make sure you select a supported image format (JPG, PNG, WEBP, GIF or AVIF) and up to 10MB',
      });
      onUploadFailed?.(
        options.userData.imageEmbed as MaybeNil<IImageEmbedFieldsFragment>,
        options.userData.context as Context,
      );
    });

    const abortAll = useAbortAll();

    const cancelHandler = () => {
      abortAll();
      setUploadState(initialUploadState);
    };

    useItemAbortListener((_, options) => {
      setUploadState(initialUploadState);
      onUploadFailed?.(
        options.userData.imageEmbed as MaybeNil<IImageEmbedFieldsFragment>,
        options.userData.context as Context,
      );
    });

    useImperativeHandle(
      ref,
      () => ({
        startUpload: (context: Context) => {
          showFileUpload({
            userData: {
              context,
            },
          });
        },
      }),
      [showFileUpload],
    );

    if (uploadState.type === UploadStateType.INACTIVE) {
      return <></>;
    }

    return (
      <UploadStateModal uploadState={uploadState} onCancel={cancelHandler} />
    );
  },
);

export default ImageEmbedUpload;
